import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useGroupsStore } from "../../../../store/admin/groupsStore";
import {
  useFilteredSocketVehicles,
  useSocketVehicleStats,
  useCompanyFilteredSocketVehicles,
  useIsSocketReseller,
  useSocketConnectionStatus,
} from "../../../../store/admin/vehiclesSocketSelector";
import { useVehiclesSocketStore } from "../../../../store/admin/vehiclesSocketStore";
import { useCompaniesStore } from "../../../../store/admin/companiesStore";
import { useActiveTripsStore } from "../../../../store/admin/activeTripsStore";

import SidebarCollapsedContent from "./SidebarCollapsedContent";
import SidebarExpandedContent from "./SidebarExpandedContent";
import ControlTowerModal from "./ControlTowerModal";
import { useAllGroups } from "../../../../features/catalogs/queries";
import { useAvailableDrivers } from "../../../../features/drivers/queries";

interface SidebarProps {
  isDarkMode: boolean;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

const MIN_WIDTH = 344;
const MAX_WIDTH = 600;
const COLLAPSED_WIDTH = 64;

export default function Sidebar({
  isDarkMode,
  isCollapsed,
  onToggleCollapse,
}: SidebarProps) {
  const { t } = useTranslation("lite");
  const [showControlTowerModal, setShowControlTowerModal] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem("sidebarWidth");
    return saved ? parseInt(saved, 10) : 512;
  });

  const sidebarRef = useRef<HTMLDivElement>(null);
  const isResizingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const selectedStatus = useVehiclesSocketStore(
    (state) => state.selectedStatus,
  );
  const selectedGroup = useVehiclesSocketStore((state) => state.selectedGroup);
  const selectedCompany = useVehiclesSocketStore(
    (state) => state.selectedCompany,
  );
  const setSelectedStatus = useVehiclesSocketStore(
    (state) => state.setSelectedStatus,
  );
  const setSelectedGroup = useVehiclesSocketStore(
    (state) => state.setSelectedGroup,
  );
  const vehicles = useFilteredSocketVehicles();
  const unitStats = useSocketVehicleStats();
  const companyFilteredVehicles = useCompanyFilteredSocketVehicles();
  const isReseller = useIsSocketReseller();
  const { loading, isConnected, awaitingCompanySelection } =
    useSocketConnectionStatus();

  // Obtener compañías para mostrar el nombre en el sidebar
  const { companies, allCompanies, fetchCompanies, fetchAllCompanies } =
    useCompaniesStore();

  const groupList = useGroupsStore((state) => state.groups);
  useAllGroups();
  useAvailableDrivers();

  useEffect(() => {
    if (isReseller) {
      fetchAllCompanies();
    } else {
      fetchCompanies({ limit: 100 });
    }
  }, [isReseller]);

  const fetchActiveTrips = useActiveTripsStore(
    (state) => state.fetchActiveTrips,
  );
  const prevTripsCompanyRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const companyChanged =
      prevTripsCompanyRef.current !== undefined &&
      prevTripsCompanyRef.current !== selectedCompany;
    prevTripsCompanyRef.current = selectedCompany;
    if (companyChanged) {
      useActiveTripsStore.getState().reset();
    }
    fetchActiveTrips();
  }, [selectedCompany, fetchActiveTrips]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchActiveTrips();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [fetchActiveTrips]);

  useEffect(() => {
    if (!isCollapsed) {
      localStorage.setItem("sidebarWidth", sidebarWidth.toString());
    }
  }, [sidebarWidth, isCollapsed]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizingRef.current) return;

    requestAnimationFrame(() => {
      const delta = e.clientX - startXRef.current;
      const newWidth = Math.min(
        MAX_WIDTH,
        Math.max(MIN_WIDTH, startWidthRef.current + delta),
      );
      setSidebarWidth(newWidth);
    });
  }, []);

  const handleMouseUp = useCallback(() => {
    isResizingRef.current = false;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";

    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  }, [handleMouseMove]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isResizingRef.current = true;
      startXRef.current = e.clientX;
      startWidthRef.current = sidebarWidth;

      document.body.style.cursor = "grab";
      document.body.style.userSelect = "none";

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    },
    [sidebarWidth, handleMouseMove, handleMouseUp],
  );

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    inputBg: isDarkMode ? "bg-dark-card" : "bg-gray-100",
    inputBorder: isDarkMode ? "border-dark-border" : "border-gray-300",
  };

  const filteredGroupList = selectedCompany
    ? groupList.filter((group) => group.company_id === selectedCompany)
    : groupList;

  const groupStats = filteredGroupList.reduce(
    (acc, group) => {
      // Contar vehículos cuyo ID está en devices_ids del grupo
      acc[group.id] = companyFilteredVehicles.filter((v) =>
        group.devices_ids?.includes(v.id),
      ).length;
      return acc;
    },
    {} as Record<string, number>,
  );

  const groups = [
    {
      id: "all",
      name: t("groups.allGroups"),
      count: companyFilteredVehicles.length,
    },
    ...filteredGroupList.map((group) => ({
      id: group.id,
      name: group.name,
      count: groupStats[group.id] || 0,
    })),
  ];

  const handleStatusClick = (status: string) => {
    setSelectedStatus(selectedStatus === status ? null : status);
  };

  return (
    <>
      <div
        ref={sidebarRef}
        className={`
          ${themeClasses.bg} 
          border-r ${themeClasses.border} 
          flex flex-col h-full 
          relative
          ${isCollapsed ? "transition-all duration-300" : ""}
        `}
        style={{
          width: isCollapsed ? COLLAPSED_WIDTH : sidebarWidth,
          minWidth: isCollapsed ? COLLAPSED_WIDTH : MIN_WIDTH,
          maxWidth: isCollapsed ? COLLAPSED_WIDTH : MAX_WIDTH,
          willChange: isCollapsed ? "auto" : "width",
        }}
      >
        {isCollapsed ? (
          <SidebarCollapsedContent
            onToggleCollapse={onToggleCollapse}
            unitStats={unitStats}
            totalVehicles={vehicles.length}
            themeClasses={themeClasses}
          />
        ) : (
          <SidebarExpandedContent
            onToggleCollapse={onToggleCollapse}
            vehicles={vehicles}
            unitStats={unitStats}
            groups={groups}
            selectedStatus={selectedStatus}
            selectedGroup={selectedGroup}
            selectedCompany={selectedCompany}
            setSelectedStatus={setSelectedStatus}
            setSelectedGroup={setSelectedGroup}
            handleStatusClick={handleStatusClick}
            isDarkMode={isDarkMode}
            themeClasses={themeClasses}
            isReseller={isReseller}
            companies={isReseller ? allCompanies : companies}
            loading={loading && !awaitingCompanySelection}
            isConnected={isConnected}
          />
        )}

        {/* Handle de resize */}
        {!isCollapsed && (
          <div
            onMouseDown={handleMouseDown}
            className={`
              absolute top-0 -right-[2px] w-[4px] h-full 
              cursor-grab active:cursor-grabbing
              transition-colors duration-150
              ${isDarkMode ? "hover:bg-blue-500/70" : "hover:bg-blue-400/70"}
              z-10
            `}
            title="Arrastra para redimensionar"
          />
        )}
      </div>

      {showControlTowerModal && (
        <ControlTowerModal
          onClose={() => setShowControlTowerModal(false)}
          themeClasses={themeClasses}
        />
      )}
    </>
  );
}
